import orderBy from "lodash/orderBy";
import { alertFactory } from "@teaming/common-ui";
import { GoalType } from "@teaming/common-enums";
import { makeMarkMap } from "@teaming/common-team";

import * as listeners from "./listeners";

import {
	AGGREGATE_ID,
	NO_TEAM,
	NO_GOAL,
	skeletonPage,
	pristineState
} from "./state";

const removeGoal = ( goalPages, goalId ) => {
	return goalPages.reduce( ( newPages, page ) => {
		return [
			...newPages,
			page.filter( goal => goal.id !== goalId ),
		];
	}, [] );
};

const updateGoal = ( goalPages, goalId, transform ) => {
	let found = false;
	const updatedPages = goalPages.reduce( ( pages, page ) => {
		const updatedPage = found ? page : page.map( goal => {
			if ( goal.id === goalId ) {
				found = true;
				return transform( goal );
			}
			return goal;
		} );
		return [
			...pages,
			updatedPage,
		];
	}, [] );
	return [ updatedPages, found, ];
};

const replaceGoal = ( goalPages, goal ) => {
	const [ updatedPages, updated, ] = updateGoal( goalPages, goal.id, _ => goal );
	if ( !updated ) {
		updatedPages.push( [ goal, ] );
	}
	return [ updatedPages, true, ];
};

export default function goalsStoreFactory( {
	storeName,
	goalStoreName,
	foundation,
	appChannels,
} ) {
	const goalStoreMutation = mutationName => `${ goalStoreName }/${ mutationName }`;

	const { store, i18n, ioSocket, ioTeam, ioGokr, } = foundation();
	const { t, } = i18n.global;

	const { success, error, info, } = alertFactory( "common-gokr.stores.Goals", i18n.global.t );
	const alerts = { success, error, info, };

	const { ioChannel, } = appChannels;
	listeners.configureSockets( {
		storeName,
		store,
		ioSocket,
		ioTeam,
		ioGokr,
		alerts,
	} );

	listeners.configureChannels( {
		storeName,
		store,
		ioChannel,
		alerts,
	} );

	const translate = ( key, args ) => t( `common-gokr.stores.Goals.${ key }`, args );

	const markMap = makeMarkMap( i18n );

	return {
		namespaced: true,
		state: pristineState(),
		getters: {
			userProfile: _ => store.getters[ "host-userProfile/userProfile" ],
			myTeams: state => state.myTeams,
			teamOptions( state ) {
				const { myTeams, teamGoalCounts, selectedGoalType, } = state;

				const teamOptions = myTeams
					.filter( team => !team.archived )
					.map( team => {
						const goalCounts = teamGoalCounts[ team.id ] || {};
						const goalCount = goalCounts[ selectedGoalType ] || 0;
						return {
							text: team.extendedName,
							value: team.id,
							mark: markMap.get( team.type ),
							count: goalCount.toString(),
							sortKey1: goalCount,
							sortKey2: team.name,
						};
					} );

				const sortedTeamOptions = orderBy(
					teamOptions,
					[ "sortKey1", "sortKey2", ],
					[ "desc", "asc", ]
				);

				return [
					{
						text: translate( "allTeamsDropDownOption" ),
						value: "",
						mark: "",
						count: "",
					},
					...sortedTeamOptions,
				];
			},
			selectedTeam( state ) {
				return state.myTeams.find( team => team.id === state.selectedTeamId ) || NO_TEAM;
			},
			selectedTeamType( state, getters ) {
				return getters.selectedTeam.type;
			},
			activeGoalCount( state ) {
				const { teamGoalCounts, selectedTeamId, } = state;
				return teamGoalCounts[ selectedTeamId || AGGREGATE_ID ]?.[ GoalType.ACTIVE ];
			},
			archivedGoalCount( state ) {
				const { teamGoalCounts, selectedTeamId, } = state;
				return teamGoalCounts[ selectedTeamId || AGGREGATE_ID ]?.[ GoalType.ARCHIVED ];
			},
			draftGoalCount( state ) {
				const { teamGoalCounts, selectedTeamId, } = state;
				return teamGoalCounts[ selectedTeamId || AGGREGATE_ID ]?.[ GoalType.DRAFT ];
			},
			selectedGoalType: state => state.selectedGoalType,
			pagedListItems( state ) {
				const goalPages = state.goalPages;
				return goalPages.reduce( ( listItems, goalPage, pageIndex ) => {
					const pageNumber = pageIndex + 1;
					const pageListItems = goalPage.map( ( goal, goalIndex ) => ( {
						goal,
						showSkeleton: !goal.id,
						keyField: `${ pageNumber }.${ goalIndex + 1 }`,
						pageNumber,
					} ) );
					return [
						...listItems,
						...pageListItems,
					];
				}, [] );
			},
			pagingOffset: state => state.pagingOffset,
			pagingLimit: state => state.pagingLimit,
			isPagingEnabled( state ) {
				if ( !state.hasPaged ) {
					return true;
				}
				return state.currentPageNumber < state.totalPageCount;
			},
			isPaging: state => state.isPaging,
			currentPageNumber: state => state.currentPageNumber,
		},
		mutations: {
			clearAll( state ) {
				Object.assign( state, pristineState() );
			},
			receiveMyTeams( state, myTeams ) {
				const patchedTeams = myTeams.map( myTeam => {
					return {
						...myTeam,
						sortKey: myTeam.name.toLowerCase(),
					};
				} );
				state.myTeams = orderBy( patchedTeams, [ "sortKey", ], [ "asc", ] );
			},
			receiveGoalMetadata( state, goalMetadata ) {
				const { teamSummary, } = goalMetadata;

				// per-team counts
				const teamGoalCounts = Object.keys( teamSummary ).reduce( ( counts, teamId ) => {
					const summaryCounts = teamSummary[ teamId ];
					counts[ teamId ] = {
						[ GoalType.ACTIVE ]: summaryCounts.activeGoalCount,
						[ GoalType.DRAFT ]: summaryCounts.draftGoalCount,
						[ GoalType.ARCHIVED ]: summaryCounts.archivedGoalCount,
					};
					return counts;
				}, {} );

				// aggregate counts
				teamGoalCounts[ AGGREGATE_ID ] = {
					[ GoalType.ACTIVE ]: goalMetadata.activeGoalCount,
					[ GoalType.DRAFT ]: goalMetadata.draftGoalCount,
					[ GoalType.ARCHIVED ]: goalMetadata.archivedGoalCount,
				};

				state.teamGoalCounts = teamGoalCounts;
			},
			pause( state, clearPages = false ) {
				if ( state.isPaging ) {
					return;
				}
				state.isPaging = true;
				if ( clearPages ) {
					state.goalPages = [
						skeletonPage(),
					];
				} else {
					state.goalPages = [
						...state.goalPages,
						skeletonPage(),
					];
				}
			},
			resume( state ) {
				if ( !state.isPaging ) {
					return;
				}
				state.isPaging = false;
				state.goalPages = removeGoal( state.goalPages, "" );
			},
			// eslint-disable-next-line no-unused-vars
			receivePagedGoals( state, { goals, offset, limit, currentPage, totalPages, } ) {
				if ( goals.length === 0 || currentPage > totalPages || totalPages === 0 ) {
					return;
				}
				state.goalPages = [
					...state.goalPages,
				];
				state.goalPages[ currentPage - 1 ] = goals;
				state.pagingOffset = offset + state.pagingLimit;
				state.currentPageNumber = currentPage;
				state.totalPageCount = totalPages;
				state.hasPaged = true;
			},
			selectGoalType( state, goalType ) {
				state.selectedGoalType = goalType;
				state.pagingOffset = 0;
				state.currentPageNumber = 0;
				state.totalPageCount = 0;
				state.goalPages = [];
				state.hasPaged = false;
			},
			selectTeam( state, teamId ) {
				if ( state.selectedTeamId === teamId ) {
					return;
				}
				state.selectedTeamId = teamId;
				state.selectedGoalType = GoalType.ACTIVE;
				state.pagingOffset = 0;
				state.currentPageNumber = 0;
				state.totalPageCount = 0;
				state.goalPages = [];
				state.hasPaged = false;
			},
			selectGoal( state, { goalId, pageNumber, } ) {
				const pageIndex = pageNumber - 1;
				const goalPage = state.goalPages[ pageIndex ];
				state.selectedGoal = goalPage.find( goal => goal.id === goalId ) || NO_GOAL;
			},
			receiveDeletedGoal( state, goalId ) {
				state.goalPages = removeGoal( state.goalPages, goalId );
			},
			receiveArchivedGoal( state, goalId ) {
				if ( state.selectedGoalType !== GoalType.ARCHIVED ) {
					state.goalPages = removeGoal( state.goalPages, goalId );
					return;
				}

				const [ updatedPages, updated, ] = updateGoal( state.goalPages, goalId, goal => ( {
					...goal,
					archived: true,
					type: GoalType.ARCHIVED,
				} ) );

				if ( updated ) {
					state.goalPages = updatedPages;
				}
			},
			receiveGoal( state, goal ) {
				// if this goal does not belong to the selected team (or all teams), discard it
				if ( ![ NO_TEAM.id, goal.teamId, ].includes( state.selectedTeamId ) ) {
					return;
				}

				// if this goal does not belong to the selected goal type, discard it
				if ( goal.type !== state.selectedGoalType ) {
					return;
				}

				const [ updatedPages, updated, ] = replaceGoal( state.goalPages, goal );

				if ( updated ) {
					state.goalPages = updatedPages;
				}
			},
		},
		actions: {
			async initialize( { getters, commit, }, teamId = "" ) {
				try {
					commit( "pause", true );

					const { pagingLimit, selectedGoalType, } = getters;
					const goalQuery = {
						offset: 0,
						limit: pagingLimit,
						type: selectedGoalType,
					};
					if ( teamId ) {
						goalQuery.teamId = teamId;
					}

					const promises = [
						ioGokr.fetchGoalMetadata(),
						ioTeam.fetchAllMyTeams( { limit: Number.MAX_SAFE_INTEGER, } ),
						ioGokr.fetchGoals( goalQuery ),
					];
					const [ metadataResponse, teamsResponse, goalsResponse, ] = await Promise.all( promises );
					const { items: goals, pageMeta, } = goalsResponse.data;
					const { offset, limit, currentPage, totalPages, } = pageMeta;

					commit( "selectTeam", teamId );
					commit( "receiveMyTeams", teamsResponse.data );
					commit( "receiveGoalMetadata", metadataResponse.data );
					commit( "receivePagedGoals", {
						goals,
						offset,
						limit,
						currentPage,
						totalPages,
					} );
				} catch ( e ) {
					error( "unexpectedError", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySelectTeam( { getters, commit, }, teamId ) {
				try {
					commit( "pause", true );

					const { pagingLimit, selectedGoalType, } = getters;
					const goalQuery = {
						offset: 0,
						limit: pagingLimit,
						type: selectedGoalType,
					};
					if ( teamId ) {
						goalQuery.teamId = teamId;
					}

					const promises = [
						ioGokr.fetchGoals( goalQuery ),
					];

					const [ goalsResponse, ] = await Promise.all( promises );

					const { items: goals, pageMeta, } = goalsResponse.data;
					const { offset, limit, currentPage, totalPages, } = pageMeta;
					commit( "selectTeam", teamId );
					commit( goalStoreMutation( "assignGoalToTeam" ), teamId, { root: true, } );
					commit( "receivePagedGoals", {
						goals,
						offset,
						limit,
						currentPage,
						totalPages,
					} );
				} catch ( e ) {
					error( "selectTeamErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async trySelectGoalType( { getters, commit, }, goalType ) {
				try {
					commit( "pause", true );

					const { pagingLimit, selectedTeam, } = getters;
					const goalQuery = {
						offset: 0,
						limit: pagingLimit,
						type: goalType,
					};
					if ( selectedTeam?.id ) {
						goalQuery.teamId = selectedTeam.id;
					}

					const promises = [
						ioGokr.fetchGoals( goalQuery ),
					];

					const [ goalsResponse, ] = await Promise.all( promises );

					const { items: goals, pageMeta, } = goalsResponse.data;
					const { offset, limit, currentPage, totalPages, } = pageMeta;

					commit( "selectGoalType", goalType );
					commit( "receivePagedGoals", {
						goals,
						offset,
						limit,
						currentPage,
						totalPages,
					} );
				} catch ( e ) {
					error( "selectGoalTypeErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
			async tryPageGoals( { getters, commit, } ) {
				try {
					commit( "pause" );

					const {
						pagingOffset,
						pagingLimit,
						selectedGoalType,
						selectedTeam,
					} = getters;

					const goalQuery = {
						offset: pagingOffset,
						limit: pagingLimit,
						type: selectedGoalType,
					};
					if ( selectedTeam?.id ) {
						goalQuery.teamId = selectedTeam.id;
					}

					const goalsResponse = await ioGokr.fetchGoals( goalQuery );

					const { items: goals, pageMeta, } = goalsResponse.data;
					const { offset, limit, currentPage, totalPages, } = pageMeta;

					commit( "receivePagedGoals", {
						goals,
						offset,
						limit,
						currentPage,
						totalPages,
					} );
				} catch ( e ) {
					error( "pageGoalsErrorNotification", e );
				} finally {
					commit( "resume" );
				}
			},
		},
	};
}
